import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  alpha,
  Fade,
  Paper,
  Popover,
  Tooltip,
  useMediaQuery,
  useTheme,
  Checkbox,
  Badge,
  Grow,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LaunchIcon from '@mui/icons-material/Launch';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import { useAuth } from '../contexts/AuthContext';
import { useChains } from '../contexts/ChainContext';
import { useNavigate } from 'react-router-dom';
import { StrawberryIcon, IconNames } from './icons';
import { debounce } from 'lodash';
import Cookies from 'js-cookie';
import NotesTimeline from './NotesTimeline';

// Common styles for scrollbars 
const commonStyles = {
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => alpha(theme.palette.primary.main, 0.2),
      borderRadius: '20px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: theme => `${alpha(theme.palette.primary.main, 0.2)} transparent`,
    '&:hover': {
      '&::-webkit-scrollbar-thumb': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    }
  },
};

// WMO weather code to label and icon (Open-Meteo)
const WMO_WEATHER = {
  0: { label: 'Clear', icon: 'sun' },
  1: { label: 'Mainly clear', icon: 'sun' },
  2: { label: 'Partly cloudy', icon: 'cloudQueue' },
  3: { label: 'Overcast', icon: 'cloud' },
  45: { label: 'Fog', icon: 'fog' },
  48: { label: 'Fog', icon: 'fog' },
  51: { label: 'Drizzle', icon: 'water' },
  53: { label: 'Drizzle', icon: 'water' },
  55: { label: 'Drizzle', icon: 'water' },
  61: { label: 'Rain', icon: 'water' },
  63: { label: 'Rain', icon: 'water' },
  65: { label: 'Rain', icon: 'water' },
  71: { label: 'Snow', icon: 'snow' },
  73: { label: 'Snow', icon: 'snow' },
  75: { label: 'Snow', icon: 'snow' },
  77: { label: 'Snow', icon: 'snow' },
  80: { label: 'Showers', icon: 'water' },
  81: { label: 'Showers', icon: 'water' },
  82: { label: 'Showers', icon: 'water' },
  85: { label: 'Snow showers', icon: 'snow' },
  86: { label: 'Snow showers', icon: 'snow' },
  95: { label: 'Thunderstorm', icon: 'thunderstorm' },
  96: { label: 'Thunderstorm', icon: 'thunderstorm' },
  99: { label: 'Thunderstorm', icon: 'thunderstorm' },
};

// Define colors array at component level
const CATEGORY_COLORS = [
  '#E40303',  // Pride Red
  '#FF8C00',  // Pride Orange  
  '#FFED00',  // Pride Yellow
  '#008018',  // Pride Green
  '#0066FF',  // Pride Blue
  '#8B00FF',  // Pride Purple
  '#E40303',  // Pride Red (repeat for more categories)
  '#FF8C00',  // Pride Orange (repeat)
  '#FFED00',  // Pride Yellow (repeat)
  '#008018',  // Pride Green (repeat)
];


// Move CategoryField to its own component file for better code splitting
const CategoryField = React.memo(({ field, label, value, onChange, readOnly, categories }) => {
  // Local state to track input value for immediate UI feedback
  const [localValue, setLocalValue] = useState(value);
  
  // Find the field definition to get the type
  const fieldDef = useMemo(() => {
    // We need to look through all categories to find the field
    // This is a simplified approach - you might need to adjust based on your data structure
    return categories.flatMap(group => group.items)
      .find(item => item.fieldName === field);
  }, [field, categories]);
  
  const isPhoneField = fieldDef?.type === 'telephone';
  const isCheckboxField = fieldDef?.type === 'checkbox';
  
  // Update local value when prop value changes
  useEffect(() => {
    // For telephone fields, ensure we always have a "+" prefix
    if (isPhoneField) {
      // If value is empty or just "+", keep it as is
      if (!value || value === '+') {
        setLocalValue(value);
      } else if (!value.startsWith('+')) {
        // Add "+" if missing
        setLocalValue(`+${value}`);
      } else {
        setLocalValue(value);
      }
    } else if (isCheckboxField) {
      // For checkbox fields, convert to boolean
      setLocalValue(value === true || value === 'true' || value === 'yes' || value === '1');
    } else {
      setLocalValue(value);
    }
  }, [value, isPhoneField, isCheckboxField]);
  
  // Use stable reference for debounced function with increased delay
  const debouncedOnChange = useMemo(
    () => debounce((newValue) => {
      onChange(field, newValue);
    }, 300), // Increased from 150ms to 300ms
    [field, onChange]
  );

  // Cleanup on unmount
  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    let newValue = e.target.value;
    
    // Special handling for telephone fields
    if (isPhoneField) {
      // Always ensure it starts with "+"
      if (newValue === '') {
        // Allow empty value
        newValue = '';
      } else if (!newValue.startsWith('+')) {
        newValue = `+${newValue}`;
      }
    }
    
    // Update local state immediately for responsive UI
    setLocalValue(newValue);
    // Debounce the actual onChange callback
    debouncedOnChange(newValue);
  }, [debouncedOnChange, isPhoneField]);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((e) => {
    const checked = e.target.checked;
    setLocalValue(checked);
    // For checkboxes, update immediately without debounce
    onChange(field, checked);
  }, [field, onChange]);

  // For telephone fields, we use a special handling to ensure "+" is preserved
  const handlePhoneKeyDown = useCallback((e) => {
    // If at position 0 and trying to delete or at position 1 and trying to backspace,
    // prevent the event to preserve the "+"
    if ((e.key === 'Delete' && e.target.selectionStart === 0) || 
        (e.key === 'Backspace' && e.target.selectionStart <= 1)) {
      e.preventDefault();
    }
  }, []);

  // Render checkbox field
  if (isCheckboxField) {
    return (
      <Checkbox
        checked={localValue === true || localValue === 'true' || localValue === 'yes' || localValue === '1'}
        onChange={handleCheckboxChange}
        disabled={readOnly}
        sx={{
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          }
        }}
      />
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={localValue}
      onChange={handleInputChange}
      onKeyDown={isPhoneField ? handlePhoneKeyDown : undefined}
      placeholder={isPhoneField ? '+1234567890' : `Enter ${label.toLowerCase()}`}
      type="text"
      InputProps={{
        readOnly,
        sx: {
          borderRadius: 3,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
          },
          '&.Mui-focused': {
            borderColor: 'primary.main',
          }
        }
      }}
      sx={{
        flex: 1,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'background.paper',
          fontSize: '0.875rem',
          fontFamily: 'StrawberrySansText',
          borderRadius: 3,
        },
        '& .MuiOutlinedInput-input': {
          padding: '8px 12px',
        }
      }}
    />
  );
});

// Pre-calculate category backgrounds
const getCategoryBackgrounds = (categories, useColoredCategories) => {
  return categories.reduce((acc, _, index) => {
    const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
    acc[index] = (theme) => {
      if (!useColoredCategories) return theme.palette.background.paper; // Switched to paper (now darker)
      const opacity = theme.palette.mode === 'dark' ? 0.15 : 0.06;
      return alpha(color, opacity);
    };
    return acc;
  }, {});
};

const HotelDetails = React.memo(({ 
  selectedHotel,
  setSelectedHotel,
  isEditing,
  handleEditClick,
  handleSaveEdit,
  handleCancelEdit,
  editedHotel,
  handleEditFieldChange,
  getCategoryValue,
  handleCopyClick,
  useColoredCategories,
  categories = [],
  isSaving,
  saveStatus = null,
  saveStatusMessage = '',
  saveStatusOpen = false,
  onSaveStatusExited,
  compactView = false,
  sidebarCollapsed = false
}) => {
  const { user } = useAuth();
  const { getChainPrimaryColor } = useChains();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Check if screen is below md breakpoint
  const [imageLoaded, setImageLoaded] = useState(false);

  const chainColor = selectedHotel ? getChainPrimaryColor(getCategoryValue(selectedHotel, 'chain')) : null;
  
  // On mobile, always use regular view regardless of compactView prop
  const shouldUseCompactView = compactView && !isMobile;
  
  // Load filters from cookies or default to all categories selected
  const [activeFilters, setActiveFilters] = useState(() => {
    const savedFilters = Cookies.get('categoryFilters');
    if (savedFilters) {
      return JSON.parse(savedFilters);
    }
    // Default to all categories selected (empty array means all are visible)
    return [];
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const isFilterPopoverOpen = Boolean(filterAnchorEl);
  const handleFilterPopoverOpen = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterPopoverClose = () => setFilterAnchorEl(null);

  // Weather: fetch once per hotel when address is available
  const weatherFetchedForHotelId = useRef(null);
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const hotelId = selectedHotel?.id;
    const hotelName = selectedHotel ? getCategoryValue(selectedHotel, 'name') : null;
    const searchQuery = (hotelName && typeof hotelName === 'string') ? hotelName.trim() : '';

    if (!hotelId || !searchQuery) {
      setWeather(null);
      if (!hotelId) weatherFetchedForHotelId.current = null;
      return;
    }

    // Only fetch once for this hotel
    if (weatherFetchedForHotelId.current === hotelId) return;
    weatherFetchedForHotelId.current = hotelId;

    let cancelled = false;
    setWeather(null);

    const fetchWeather = async () => {
      try {
        // Geocode by hotel name (Nominatim requires User-Agent)
        const geoRes = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1`,
          { headers: { 'User-Agent': 'HotelManager/1.0 (contact@hotelmanager.app)' } }
        );
        if (cancelled) return;
        const geoData = await geoRes.json();
        if (!Array.isArray(geoData) || geoData.length === 0) return;
        const { lat, lon } = geoData[0];

        // Weather: Open-Meteo (no API key)
        const weatherRes = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,relative_humidity_2m`
        );
        if (cancelled) return;
        const weatherData = await weatherRes.json();
        if (!weatherData?.current) return;
        const { temperature_2m, weather_code, relative_humidity_2m } = weatherData.current;
        const info = WMO_WEATHER[weather_code] || { label: 'Unknown', icon: 'cloud' };
        setWeather({
          temp: temperature_2m,
          code: weather_code,
          condition: info.label,
          icon: info.icon,
          humidity: relative_humidity_2m,
        });
      } catch (err) {
        // On error, leave weather as null (nothing shown)
      } finally {
        // no loading state – fetch is quick
      }
    };

    fetchWeather();
    return () => { cancelled = true; };
  }, [selectedHotel, getCategoryValue]);

  // Memoize category backgrounds
  const categoryBackgrounds = useMemo(
    () => getCategoryBackgrounds(categories, useColoredCategories),
    [categories, useColoredCategories]
  );

  // Memoize field change handler with useCallback
  const handleFieldChange = useCallback((field, value) => {
    handleEditFieldChange(field, value);
  }, [handleEditFieldChange]);

  // Handle adding a new note
  const handleAddNote = note => {
    if (!selectedHotel || !note.content) return;
    
    const newNote = {
      id: Date.now().toString(),
      content: note.content,
      categoryId: note.categoryId || '',
      createdBy: user.email,
      createdAt: new Date(),
      updatedAt: new Date(),
      user: {
        id: user.uid,
        name: user.displayName || user.email.split('@')[0],
        avatar: user.photoURL
      }
    };
    
    // Get existing notes or initialize as empty array if none exist
    const existingNotes = getCategoryValue(selectedHotel, 'notes') || [];
    
    // Ensure existingNotes is an array
    const updatedNotes = [newNote, ...(Array.isArray(existingNotes) ? existingNotes : [])];
    
    handleFieldChange('notes', updatedNotes);
  };

  // Handle editing an existing note
  const handleEditNote = (noteId, updatedFields) => {
    if (!selectedHotel) return;

    // Get existing notes or initialize as empty array if none exist
    const existingNotes = getCategoryValue(selectedHotel, 'notes') || [];
    
    // Ensure existingNotes is an array
    if (!Array.isArray(existingNotes)) {
      console.warn('Notes is not an array:', existingNotes);
      return;
    }

    const updatedNotes = existingNotes.map(note => {
      if (note.id === noteId) {
        return {
          ...note,
          content: updatedFields.content,
          categoryId: updatedFields.categoryId,
          updatedAt: updatedFields.updatedAt || new Date(),
          // Preserve the original user data who created the note
          user: note.user || {
            id: user.uid,
            name: user.displayName || user.email.split('@')[0],
            avatar: user.photoURL
          }
        };
      }
      return note;
    });
    
    handleFieldChange('notes', updatedNotes);
  };

  // Handle deleting a note
  const handleDeleteNote = noteId => {
    if (!selectedHotel) return;
    
    // Get existing notes or initialize as empty array if none exist
    const existingNotes = getCategoryValue(selectedHotel, 'notes') || [];
    
    // Handle legacy notes (stored as string)
    // If deleting a legacy note, clear the notes field entirely
    if (noteId === 'legacy-note') {
      handleFieldChange('notes', []);
      return;
    }
    
    // Ensure existingNotes is an array
    if (!Array.isArray(existingNotes)) {
      console.warn('Notes is not an array:', existingNotes);
      return;
    }
    
    const updatedNotes = existingNotes.filter(
      note => note.id !== noteId
    );
    
    handleFieldChange('notes', updatedNotes);
  };

  // Get notes in the correct format
  const getNotes = useCallback(() => {
    try {
      const notes = getCategoryValue(isEditing ? editedHotel : selectedHotel, 'notes');
      
      // If notes is null, undefined, or not available, return empty array
      if (!notes) return [];
      
      // If notes is a string (legacy format), convert it to the new format
      if (typeof notes === 'string' && notes) {
        return [{ 
          id: 'legacy-note', 
          content: notes, 
          createdBy: 'Unknown', 
          // Don't add timestamps for legacy notes
          createdAt: null, 
          updatedAt: null
        }];
      }
      
      // Return notes array or empty array if notes is not an array
      if (!Array.isArray(notes)) {
        console.warn('Notes is not an array:', notes);
        return [];
      }
      
      return notes;
    } catch (error) {
      console.error('Error getting notes:', error);
      return [];
    }
  }, [selectedHotel, editedHotel, isEditing, getCategoryValue]);

  // Count notes for a specific category
  const getNotesCountForCategory = useCallback((categoryId) => {
    if (!categoryId) return 0;
    const notes = getNotes();
    return notes.filter(note => note.categoryId === categoryId).length;
  }, [getNotes]);

  // Reference to the notes section
  const notesSectionRef = React.useRef(null);

  // Function to scroll to notes section
  const scrollToNotes = useCallback(() => {
    if (notesSectionRef.current) {
      notesSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Memoize expensive computations
  const groupedFields = useMemo(() => {
    return categories.map(group => ({
      ...group,
      fields: (group?.items || [])
        .filter(item => !item?.isArchived)
        // Filter out password fields if user doesn't have permission
        .filter(item => {
          // Check if password type and user doesn't have permission
          // Admins can always view password fields
          if (item.type === 'password' && 
              user?.role !== 'admin' && 
              !(user?.permissions || []).includes('view-passwords')) {
            return false;
          }
          return isEditing || getCategoryValue(selectedHotel, item?.fieldName);
        })
    })).filter(group => group.fields.length > 0);
  }, [categories, isEditing, selectedHotel, getCategoryValue, user?.permissions, user?.role]);

  // Filter groups based on active filters (inverted logic - show all except those in activeFilters)
  const filteredGroups = useMemo(() => {
    if (activeFilters.length === 0) return groupedFields; // Show all when no filters are "unselected"
    return groupedFields.filter(group => !activeFilters.includes(group.id)); // Show all except unselected ones
  }, [groupedFields, activeFilters]);

  // Toggle filter for a category group and save to cookies (now represents "unselected" categories)
  const toggleFilter = useCallback((groupId) => {
    setActiveFilters(prev => {
      const newFilters = prev.includes(groupId)
        ? prev.filter(id => id !== groupId) // Remove from unselected (show it)
        : [...prev, groupId]; // Add to unselected (hide it)
      
      // Save to cookies
      Cookies.set('categoryFilters', JSON.stringify(newFilters), { expires: 365 });
      return newFilters;
    });
  }, []);

  // Clear all filters and update cookies
  const clearFilters = useCallback(() => {
    setActiveFilters([]);
    Cookies.set('categoryFilters', JSON.stringify([]), { expires: 365 });
  }, []);

  // Manual column balancing for compact view
  const distributeCardsIntoColumns = useCallback((groups) => {
    if (!shouldUseCompactView) return { column1: groups, column2: [] };
    
    // Estimate card height based on number of fields
    const getEstimatedHeight = (group) => {
      const baseHeight = 120; // Base height for title and padding
      const fieldHeight = 60; // Estimated height per field
      return baseHeight + (group.fields.length * fieldHeight);
    };
    
    const column1 = [];
    const column2 = [];
    let column1Height = 0;
    let column2Height = 0;
    
    // Sort groups by estimated height (largest first) for better distribution
    const sortedGroups = [...groups].sort((a, b) => 
      getEstimatedHeight(b) - getEstimatedHeight(a)
    );
    
    // Distribute cards using a greedy algorithm
    sortedGroups.forEach(group => {
      const estimatedHeight = getEstimatedHeight(group);
      
      if (column1Height <= column2Height) {
        column1.push(group);
        column1Height += estimatedHeight;
      } else {
        column2.push(group);
        column2Height += estimatedHeight;
      }
    });
    
    return { column1, column2 };
  }, [shouldUseCompactView]);

  // Get distributed columns
  const { column1, column2 } = useMemo(() => 
    distributeCardsIntoColumns(filteredGroups), 
    [filteredGroups, distributeCardsIntoColumns]
  );

  // Render card function
  const renderCard = (group, groupIndex, index) => {
    const fields = group.fields;
    const hasValues = isEditing || fields.some(item => 
      getCategoryValue(selectedHotel, item?.fieldName)
    );
    if (!hasValues) return null;

    return (
      <Box 
        key={group?.id || 'default'} 
        sx={{ 
          mb: 3,
          breakInside: compactView ? 'avoid' : 'auto',
          pageBreakInside: compactView ? 'avoid' : 'auto',
          columnBreakInside: compactView ? 'avoid' : 'auto',
          display: compactView ? 'inline-block' : 'block',
          width: compactView ? '100%' : 'auto',
          '&:last-child': {
            mb: 0,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: theme => useColoredCategories 
              ? categoryBackgrounds[index](theme)
              : theme.palette.background.paper,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            height: 'fit-content',
            px: { xs: 3, sm: 0 },
            boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
              '& .field-row': {
                '&:hover': {
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
                  borderRadius: 2,
                  mx: -1,
                  px: 1
                }
              }
            }
          }}
        >
          {/* Category Header */}
          <Box sx={{
            px: compactView ? 3 : 4,
            pt: compactView ? 3 : 4,
            pb: 1.5,
            bgcolor: theme => alpha(theme.palette.background.default, 0.02),
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: compactView ? 24 : 32,
              right: compactView ? 24 : 32,
              height: '1px',
              bgcolor: 'divider'
            }
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 500,
                color: 'text.primary',
                fontSize: '2rem',
                opacity: 0.9
              }}
            >
              {group?.title || 'Untitled Group'}
            </Typography>
          </Box>
          
          <Box sx={{ p: compactView ? 3 : 4, display: 'flex', flexDirection: 'column', gap: compactView ? 1.5 : 2 }}>
            {fields.map(item => {
              if (!item) return null;
              
              // In edit mode, show all fields. Otherwise, only show fields with values
              const value = getCategoryValue(selectedHotel, item.fieldName);
              // For checkbox fields, always show them (even if unchecked)
              const isCheckboxField = item.type === 'checkbox';
              if (!isEditing && !value && !isCheckboxField) return null;
              
              // Special handling for chain field
              if (item.fieldName === 'chain') {
                if (isEditing) {
                  return (
                    <Box
                      key={item.id || 'chain'}
                      className="field-row"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: compactView ? 1.5 : 2,
                        pb: compactView ? 1.5 : 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.2s ease',
                        minHeight: compactView ? 48 : 56,
                        '&:last-child': {
                          borderBottom: 'none',
                          pb: 0,
                        },
                      }}
                    >
                      <Box sx={{ width: compactView ? 140 : 160, flexShrink: 0 }}>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            fontFamily: 'StrawberrySansText',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontSize: compactView ? '0.7rem' : '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <CategoryField
                          field={item.fieldName}
                          label={item.title}
                          value={editedHotel ? getCategoryValue(editedHotel, item.fieldName) || '' : ''}
                          onChange={handleFieldChange}
                          readOnly={false}
                          categories={categories}
                        />
                      </Box>
                    </Box>
                  );
                } else if (value) {
                  return (
                    <Box
                      key={item.id || 'chain'}
                      className="field-row"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: compactView ? 1.5 : 2,
                        pb: compactView ? 1.5 : 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.2s ease',
                        minHeight: compactView ? 48 : 56,
                        '&:last-child': {
                          borderBottom: 'none',
                          pb: 0,
                        },
                      }}
                    >
                      <Box sx={{ width: compactView ? 140 : 160, flexShrink: 0 }}>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            fontFamily: 'StrawberrySansText',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontSize: compactView ? '0.7rem' : '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        minHeight: compactView ? 28 : 32 
                      }}>
                        <Box sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexWrap: 'wrap'
                      }}>
                        <Typography 
                          variant="body1"
                          sx={{
                            fontFamily: 'StrawberrySansText',
                            fontSize: compactView ? '0.8125rem' : '0.875rem',
                            lineHeight: 1.5,
                            color: 'text.primary',
                            wordBreak: 'break-word',
                            '&::selection': {
                              backgroundColor: 'primary.main',
                              color: 'background.paper',
                            }
                          }}
                        >
                          {value}
                        </Typography>
                      {item.isCopyable && value && (
                        <Tooltip title={`Copy ${item.title}`} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyClick(value, item.title, item.type)}
                            sx={{ 
                              color: 'text.secondary',
                              opacity: 0.6,
                              p: 0.5,
                              borderRadius: 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'primary.main',
                                opacity: 1,
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <StrawberryIcon 
                              name={IconNames.COPY} 
                              size={14}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                        </Box>
                      </Box>
                    </Box>
                  );
                }
              }
              
              // Default rendering for other fields
              return (
                <Box
                  key={item.id || item.fieldName}
                  className="field-row"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: compactView ? 1.5 : 2,
                    pb: compactView ? 1.5 : 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease',
                    minHeight: compactView ? 48 : 56,
                    '&:last-child': {
                      borderBottom: 'none',
                      pb: 0,
                    },
                  }}
                >
                  <Box sx={{ 
                    width: compactView ? 140 : 160, 
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.75
                  }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        fontFamily: 'StrawberrySansText',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontSize: compactView ? '0.7rem' : '0.75rem',
                        fontWeight: 500,
                        display: 'block',
                        width: '100%'
                      }}
                    >
                      {item.title}
                    </Typography>
                    
                    {!isEditing && getNotesCountForCategory(item.fieldName) > 0 && (
                      <Tooltip title={`View ${getNotesCountForCategory(item.fieldName)} note${getNotesCountForCategory(item.fieldName) > 1 ? 's' : ''} about this category`}>
                        <Box
                          component="span"
                          onClick={scrollToNotes}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            m: 0,
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            borderRadius: 4,
                            height: 18,
                            px: 1,
                            fontSize: '0.65rem',
                            fontWeight: 'medium',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid',
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              transform: 'translateY(-1px)',
                              boxShadow: theme => `0 2px 4px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          <EditOutlinedIcon 
                            sx={{ 
                              fontSize: '0.7rem', 
                              mr: 0.3,
                              width: '0.7rem',
                              height: '0.7rem'
                            }} 
                          />
                          <strong>{getNotesCountForCategory(item.fieldName)}</strong>&nbsp;{getNotesCountForCategory(item.fieldName) === 1 ? 'NOTE' : 'NOTES'}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  {isEditing ? (
                    <CategoryField
                      field={item.fieldName}
                      label={item.title}
                      value={editedHotel ? getCategoryValue(editedHotel, item.fieldName) || '' : ''}
                      onChange={handleFieldChange}
                      readOnly={false}
                      categories={categories}
                    />
                  ) : (
                    <Box sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      minHeight: compactView ? 28 : 32,
                      justifyContent: 'flex-start'
                    }}>
                      <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        alignItems: 'center',
                        minHeight: compactView ? 28 : 32 
                      }}>
                        <Box sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexWrap: 'wrap'
                    }}>
                      <Typography 
                        variant="body1"
                        component={item.type === 'url' ? 'div' : 'div'}
                        sx={{
                          fontFamily: 'StrawberrySansText',
                          fontSize: compactView ? '0.8125rem' : '0.875rem',
                          lineHeight: 1.5,
                          color: 'text.primary',
                          wordBreak: 'break-word',
                          '&::selection': {
                            backgroundColor: 'primary.main',
                            color: 'background.paper',
                          }
                        }}
                      >
                        {item.type === 'checkbox' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                              checked={value === true || value === 'true' || value === 'yes' || value === '1'}
                              disabled
                              sx={{
                                padding: 0.5,
                                '& .MuiSvgIcon-root': {
                                  fontSize: '1.25rem',
                                }
                              }}
                            />
                          </Box>
                        ) : item.type === 'url' && value ? (
                          <Button
                            component="a"
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                            endIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              textTransform: 'none',
                              fontFamily: 'StrawberrySansText',
                              fontSize: '0.875rem',
                              borderColor: 'divider',
                              color: 'primary.main',
                              borderRadius: 2,
                              px: 2,
                              py: 0.5,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                                transform: 'translateY(-1px)',
                                boxShadow: theme => `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            }}
                          >
                            {item.title}
                          </Button>
                        ) : item.type === 'telephone' && value ? (
                          <Button
                            component="a"
                            href={`tel:${value.startsWith('+') ? value : `+${value}`}`}
                            variant="outlined"
                            size="small"
                            endIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              textTransform: 'none',
                              fontFamily: 'StrawberrySansText',
                              fontSize: '0.875rem',
                              borderColor: 'divider',
                              color: 'primary.main',
                              borderRadius: 2,
                              px: 2,
                              py: 0.5,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                                transform: 'translateY(-1px)',
                                boxShadow: theme => `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            }}
                          >
                            {value.startsWith('+') ? value : `+${value}`}
                          </Button>
                        ) : (
                          <div style={{ pointerEvents: 'auto' }}>
                            {value || '-'}
                          </div>
                        )}
                      </Typography>
                      {item.isCopyable && value && (
                        <Tooltip title={`Copy ${item.title}`} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyClick(value, item.title, item.type)}
                            sx={{ 
                              color: 'text.secondary',
                              opacity: 0.6,
                              p: 0.5,
                              borderRadius: 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'primary.main',
                                opacity: 1,
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <StrawberryIcon 
                              name={IconNames.COPY} 
                              size={14}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: { xs: 'calc(100vh - 70px)', sm: 'calc(100vh - 85px)' }, // Account for header height
      overflowY: 'auto', // Make entire page scrollable
      ...commonStyles.scrollbar,
      p: { xs: 2, sm: 4 }, // Consistent padding on all sides
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transition for content area
      transform: 'translateZ(0)', // Force hardware acceleration for smoother animations
    }}>
      {/* Mobile Back Button */}
      <Box sx={{ 
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center',
        px: 2,
        py: 1,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{ 
            color: 'primary.main',
            textTransform: 'none',
            fontFamily: 'StrawberrySansText',
            fontSize: '0.875rem',
            fontWeight: 500,
            borderRadius: 9999,
            bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              bgcolor: theme => alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          Back
        </Button>
      </Box>

      {/* Hotel content */}
      <Fade in={true}>
        <Box sx={{ 
          px: { xs: 2, sm: 0 }, // Use consistent padding regardless of compact view
          pb: { xs: 4, sm: 0 }
        }}>
          {/* Banner/Cover Image */}
          <Box 
            className="banner-container"
            sx={{
              mb: { xs: 2, sm: 2 },
              position: 'relative',
              height: { xs: 160, sm: 220 },
              width: '100%',
              overflow: 'hidden',
              borderRadius: { xs: 3, sm: 3 },
              bgcolor: 'background.default',
              boxShadow: theme => `0 4px 24px ${alpha(theme.palette.common.black, 0.08)}`,
              mt: { xs: 2, sm: 0 },
              transition: 'all 0.3s ease-in-out',
              WebkitMaskImage: '-webkit-radial-gradient(white, black)',
              transform: 'translateZ(0)',
            }}
          >
            {/* Loading Placeholder */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                display: !imageLoaded ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background: theme => `
                    linear-gradient(
                      90deg,
                      ${alpha(theme.palette.background.default, 0)} 0%,
                      ${alpha(theme.palette.background.default, 0.1)} 50%,
                      ${alpha(theme.palette.background.default, 0)} 100%
                    )
                  `,
                  animation: 'shimmer 1.5s infinite',
                  '@keyframes shimmer': {
                    '0%': {
                      transform: 'translateX(-100%)',
                    },
                    '100%': {
                      transform: 'translateX(100%)',
                    },
                  },
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '3px solid',
                  borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                  borderTopColor: 'primary.main',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              />
            </Box>

            {/* Actual Banner Image */}
            {getCategoryValue(selectedHotel, 'coverimageurl') && (
              <Box
                component="img"
                src={getCategoryValue(selectedHotel, 'coverimageurl')}
                alt={getCategoryValue(selectedHotel, 'name')}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                }}
              />
            )}

            {/* Chain color overlay (blend mode tints the image) */}
            {chainColor && getCategoryValue(selectedHotel, 'coverimageurl') && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: chainColor,
                  opacity: 1,
                  mixBlendMode: 'color',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            )}

            {/* Gradient Overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: theme => `
                  linear-gradient(180deg,
                    ${alpha(theme.palette.background.default, .2)} 0%,
                    ${alpha(theme.palette.background.default, .2)} 50%,
                    ${alpha(theme.palette.background.default, .2)} 100%
                  )
                `,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />

            {/* Centered Text Content */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
                textAlign: 'center',
                color: 'white',
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
              }}
            >
              {isEditing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
                  <TextField
                    value={getCategoryValue(editedHotel, 'name') || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    variant="standard"
                    placeholder="Hotel Name"
                    sx={(theme) => ({
                      '& .MuiInputBase-input': {
                        color: 'white',
                        fontSize: { xs: '2rem', sm: '4.5rem' },
                        fontWeight: 400,
                        fontFamily: 'StrawberrySansDisplay',
                        lineHeight: 1.2,
                        padding: 0,
                        letterSpacing: '-0.01em',
                        textAlign: 'center',
                        '&::placeholder': {
                          color: alpha(theme.palette.common.white, 0.7),
                          opacity: 1
                        }
                      },
                      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                        display: 'none'
                      }
                    })}
                  />
                  <TextField
                    value={getCategoryValue(editedHotel, 'address') || ''}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                    variant="standard"
                    placeholder="Hotel Address"
                    sx={(theme) => ({
                      width: '100%',
                      maxWidth: '500px',
                      '& .MuiInputBase-input': {
                        color: alpha(theme.palette.common.white, 0.9),
                        fontFamily: 'StrawberrySansText',
                        padding: 0,
                        textAlign: 'center',
                        fontSize: '1rem',
                        '&::placeholder': {
                          color: alpha(theme.palette.common.white, 0.6),
                          opacity: 1
                        }
                      },
                      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                        display: 'none'
                      }
                    })}
                  />
                  <TextField
                    value={getCategoryValue(editedHotel, 'coverimageurl') || ''}
                    onChange={(e) => handleFieldChange('coverimageurl', e.target.value)}
                    variant="standard"
                    placeholder="Cover Image URL"
                    size="small"
                    sx={(theme) => ({
                      width: '100%',
                      maxWidth: '500px',
                      '& .MuiInputBase-input': {
                        color: alpha(theme.palette.common.white, 0.7),
                        fontSize: '0.75rem',
                        fontFamily: 'StrawberrySansText',
                        padding: 0,
                        textAlign: 'center',
                        '&::placeholder': {
                          color: alpha(theme.palette.common.white, 0.5),
                          opacity: 1
                        }
                      },
                      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                        display: 'none'
                      }
                    })}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: 'StrawberrySansDisplay',
                      fontWeight: 400,
                      fontSize: { xs: '2rem', sm: '4.5rem' },
                      lineHeight: 1.2,
                      color: 'white',
                      letterSpacing: '-0.01em',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: { xs: '90vw', sm: '70vw' }
                    }}
                  >
                    {getCategoryValue(selectedHotel, 'name')}
                  </Typography>
                  {getCategoryValue(selectedHotel, 'address') && (
                    <Button
                      component="a"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getCategoryValue(selectedHotel, 'address'))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<StrawberryIcon name={IconNames.LOCATION_PIN} size={20} />}
                      sx={{ 
                        color: 'white',
                        textTransform: 'none',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontFamily: 'StrawberrySansText',
                        opacity: 0.9,
                        p: 0,
                        minWidth: 'auto',
                        justifyContent: 'center',
                        maxWidth: { xs: '90vw', sm: '70vw' },
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '& .MuiButton-startIcon': {
                          ml: 0,
                          mr: 0.75
                        },
                        '&:hover': {
                          color: 'white',
                          opacity: 1,
                          bgcolor: 'transparent'
                        }
                      }}
                    >
                      {getCategoryValue(selectedHotel, 'address')}
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {saveStatus && (
                <Grow
                  in={saveStatusOpen}
                  timeout={{ enter: 280, exit: 220 }}
                  onExited={onSaveStatusExited}
                  style={{ transformOrigin: 'right center' }}
                >
                  <Chip
                    size="small"
                    icon={saveStatus === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={saveStatus === 'success' ? 'Saved' : saveStatusMessage}
                    color={saveStatus === 'success' ? 'success' : 'error'}
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      boxShadow: (theme) =>
                        saveStatus === 'success'
                          ? `0 2px 12px ${alpha(theme.palette.success.main, 0.35)}`
                          : `0 2px 12px ${alpha(theme.palette.error.main, 0.35)}`,
                      '& .MuiChip-icon': {
                        color: 'inherit',
                        fontSize: '1rem',
                      },
                    }}
                  />
                </Grow>
              )}
              {isEditing ? (
                <>
                  <Button
                    variant="contained"
                    onClick={handleCancelEdit}
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                      backdropFilter: 'blur(4px)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                      },
                      px: 2,
                      py: 0.75,
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      borderRadius: 1.5,
                      minHeight: 36
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      backdropFilter: 'blur(4px)',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      px: 2,
                      py: 0.75,
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      borderRadius: 1.5,
                      minHeight: 36
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                user?.role !== 'view-only' && (
                  <Button
                    onClick={handleEditClick}
                    variant="contained"
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    sx={{ 
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                      backdropFilter: 'blur(8px)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fontFamily: 'StrawberrySansText',
                      textTransform: 'none',
                      minHeight: 36,
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '& .MuiButton-startIcon': {
                        marginRight: 0.75,
                        marginLeft: 0
                      }
                    }}
                  >
                    Edit
                  </Button>
                )
              )}
            </Box>
          </Box>

          {/* Category Group Filters - Compact trigger + Popover */}
          {(
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              mb: { xs: compactView ? 1.5 : 2, sm: compactView ? 2 : 3 },
              minHeight: 'calc(100vh - 200px)',
            }}>
              {/* Toolbar bar: Categories label + Filter */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1.25,
                  px: { xs: 1, sm: 1.5 },
                  mb: 2.25,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                }}
              >
                <Tooltip title="Filter categories">
                  <Badge badgeContent={activeFilters.length} color="primary" invisible={activeFilters.length === 0}>
                    <Button
                      size="small"
                      onClick={handleFilterPopoverOpen}
                      aria-label="Filter categories"
                      aria-describedby={isFilterPopoverOpen ? 'filter-popover' : undefined}
                      startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        textTransform: 'none',
                        fontFamily: 'StrawberrySansText',
                        fontSize: '0.8125rem',
                        borderRadius: 2,
                        py: 0.75,
                        px: 1.5,
                        minHeight: 36,
                        ...(activeFilters.length > 0
                          ? {
                              color: 'primary.main',
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                              '&:hover': {
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                                color: 'primary.main',
                              },
                            }
                          : {
                              color: 'text.secondary',
                              bgcolor: 'transparent',
                              '&:hover': {
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main',
                              },
                            }),
                      }}
                    >
                      Filter Categories
                    </Button>
                  </Badge>
                </Tooltip>
                {/* Weather at hotel (fetched once when hotel is displayed, by hotel name) */}
                {selectedHotel && getCategoryValue(selectedHotel, 'name') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {weather && (
                      <Grow in timeout={400}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Tooltip title={`${weather.condition}, ${weather.humidity}% humidity`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {weather.icon === 'sun' && <WbSunnyIcon sx={{ fontSize: 20, color: 'warning.main' }} />}
                              {weather.icon === 'cloud' && <CloudIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
                              {weather.icon === 'cloudQueue' && <CloudQueueIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
                              {weather.icon === 'snow' && <AcUnitIcon sx={{ fontSize: 20, color: 'info.main' }} />}
                              {weather.icon === 'water' && <WaterDropIcon sx={{ fontSize: 20, color: 'info.main' }} />}
                              {weather.icon === 'thunderstorm' && <ThunderstormIcon sx={{ fontSize: 20, color: 'warning.dark' }} />}
                              {weather.icon === 'fog' && <CloudIcon sx={{ fontSize: 20, color: 'text.secondary', opacity: 0.8 }} />}
                              <Typography variant="body2" sx={{ fontFamily: 'StrawberrySansText', fontWeight: 500 }}>
                                {Math.round(weather.temp)}°C
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  fontFamily: 'StrawberrySansText',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  color: 'text.secondary',
                                  letterSpacing: '0.02em',
                                  textTransform: 'lowercase',
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: theme => alpha(theme.palette.text.secondary, 0.08),
                                }}
                              >
                                {weather.condition}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Grow>
                    )}
                  </Box>
                )}
              </Box>

              <Popover
                id="filter-popover"
                open={isFilterPopoverOpen}
                anchorEl={filterAnchorEl}
                onClose={handleFilterPopoverClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    borderRadius: 3,
                    boxShadow: theme => `0 4px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                    minWidth: 240,
                    maxWidth: 320,
                    maxHeight: 'min(70vh, 400px)',
                    overflow: 'auto',
                    outline: 'none',
                    border: 'none',
                    ...commonStyles.scrollbar,
                  },
                }}
              >
                <Box sx={{ px: 2, py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FilterListIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontFamily: 'StrawberrySansDisplay', fontWeight: 500, fontSize: '0.875rem', color: 'text.primary' }}>
                      Filter Categories
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontFamily: 'StrawberrySansText', lineHeight: 1.4 }}>
                    Toggle categories to show or hide them below.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {groupedFields.map((group, index) => {
                      const isUnselected = activeFilters.includes(group.id);
                      const color = useColoredCategories ? CATEGORY_COLORS[index % CATEGORY_COLORS.length] : '#1976d2';
                      return (
                        <Button
                          key={group.id}
                          size="small"
                          disableElevation
                          onClick={() => toggleFilter(group.id)}
                          variant={isUnselected ? 'outlined' : 'contained'}
                          sx={{
                            width: '100%',
                            height: 36,
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: '0.75rem',
                            fontFamily: 'StrawberrySansText',
                            fontWeight: isUnselected ? 500 : 600,
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            transition: 'all 0.2s ease',
                            ...(isUnselected ? {
                              color: useColoredCategories ? color : 'primary.main',
                              borderColor: useColoredCategories ? alpha(color, 0.4) : theme => alpha(theme.palette.primary.main, 0.4),
                              backgroundColor: 'transparent',
                              '&:hover': {
                                borderColor: useColoredCategories ? color : 'primary.main',
                                backgroundColor: useColoredCategories ? alpha(color, 0.08) : theme => alpha(theme.palette.primary.main, 0.08),
                                transform: 'translateY(-1px)',
                              },
                            } : {
                              backgroundColor: useColoredCategories ? alpha(color, 0.1) : theme => alpha(theme.palette.primary.main, 0.1),
                              color: useColoredCategories ? color : 'primary.main',
                              borderColor: useColoredCategories ? alpha(color, 0.1) : theme => alpha(theme.palette.primary.main, 0.1),
                              boxShadow: useColoredCategories ? `0 2px 6px ${alpha(color, 0.05)}` : theme => `0 2px 6px ${alpha(theme.palette.primary.main, 0.05)}`,
                              '&:hover': {
                                backgroundColor: useColoredCategories ? alpha(color, 0.15) : theme => alpha(theme.palette.primary.main, 0.15),
                                boxShadow: useColoredCategories ? `0 3px 8px ${alpha(color, 0.08)}` : theme => `0 3px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transform: 'translateY(-1px)',
                              },
                            }),
                            '&:active': { transform: 'translateY(0)', transition: 'all 0.1s ease' },
                          }}
                        >
                          {group.title}
                        </Button>
                      );
                    })}
                    {activeFilters.length > 0 && (
                      <>
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.5, pt: 1.5 }}>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => { clearFilters(); handleFilterPopoverClose(); }}
                            sx={{
                              width: '100%',
                              height: 32,
                              px: 2,
                              py: 0.5,
                              borderRadius: 2,
                              fontSize: '0.75rem',
                              fontFamily: 'StrawberrySansText',
                              textTransform: 'none',
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'primary.main',
                                backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            Show All
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </Popover>

              {/* Main Content Area */}
              <Box sx={{ 
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}>

                {/* Information Groups */}
                {shouldUseCompactView ? (
                  // Two-column layout with manual balancing
                  <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    columnGap: { xs: 0, md: 3 }, // Only apply gap between columns, not rows
                    alignItems: 'start',
                    alignContent: 'start', // Align grid content to top
                  }}>
                    {/* Notes Section */}
                    {(isEditing || (selectedHotel && (getCategoryValue(selectedHotel, 'notes') || []).length > 0)) && (
                      <Box sx={{
                        gridColumn: '1 / -1', // Span both columns
                        mb: { xs: 2, sm: 1.5 }, // Consistent spacing below notes section
                      }}>
                        <Box sx={{
                          position: 'relative',
                          px: { xs: 2, sm: 0 },
                          display: 'flex',
                          flexDirection: 'column',
                          gap: { xs: compactView ? 0.75 : 1, sm: compactView ? 1 : 1.5 },
                          bgcolor: theme => theme.palette.background.paper,
                          borderRadius: 3,
                          p: { xs: compactView ? 1.25 : 1.5, sm: compactView ? 1.5 : 2 },
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pl: 0.5
                          }}>
                            <Box sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1.5,
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{
                                flexShrink: 0,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                fontFamily: 'StrawberrySansDisplay'
                              }}
                            >
                              Notes
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <NotesTimeline
                              notes={getNotes()}
                              onAddNote={handleAddNote}
                              onEditNote={handleEditNote}
                              onDeleteNote={handleDeleteNote}
                              readOnly={!isEditing}
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* No matching categories message - inside grid */}
                    {filteredGroups.length === 0 && activeFilters.length > 0 && (
                      <Box 
                        sx={{ 
                          gridColumn: '1 / -1', // Span both columns
                          p: 4, 
                          textAlign: 'center', 
                          color: 'text.secondary',
                          bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                          borderRadius: 3,
                          border: '1px dashed',
                          borderColor: 'divider',
                        }}
                      >
                        <FilterListIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                        <Typography variant="body1">No matching categories</Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                          Try selecting different filters or clear all filters
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={clearFilters}
                          sx={{ mt: 2 }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                    )}

                    {/* Column 1 */}
                    <Box>
                      {column1.map((group, groupIndex) => {
                        return renderCard(group, groupIndex, filteredGroups.indexOf(group));
                      })}
                    </Box>
                    
                    {/* Column 2 - Only show on desktop */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      {column2.map((group, groupIndex) => {
                        return renderCard(group, groupIndex, filteredGroups.indexOf(group));
                      })}
                    </Box>
                  </Box>
                ) : (
                  // Single column layout for regular view
                  <Box sx={{ 
                    pb: 2,
                  }}>
                    {/* Notes Section */}
                    {(isEditing || (selectedHotel && (getCategoryValue(selectedHotel, 'notes') || []).length > 0)) && (
                      <Box sx={{ mb: { xs: 2, sm: 1 } }}>
                        <Box sx={{
                          position: 'relative',
                          px: { xs: 2, sm: 0 },
                          display: 'flex',
                          flexDirection: 'column',
                          gap: { xs: compactView ? 0.75 : 1, sm: compactView ? 1 : 1.5 },
                          bgcolor: theme => theme.palette.background.paper,
                          borderRadius: 3,
                          p: { xs: compactView ? 1.25 : 1.5, sm: compactView ? 1.5 : 2 },
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pl: 0.5
                          }}>
                            <Box sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1.5,
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{
                                flexShrink: 0,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                fontFamily: 'StrawberrySansDisplay'
                              }}
                            >
                              Notes
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <NotesTimeline
                              notes={getNotes()}
                              onAddNote={handleAddNote}
                              onEditNote={handleEditNote}
                              onDeleteNote={handleDeleteNote}
                              readOnly={!isEditing}
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* No matching categories message - inside regular view */}
                    {filteredGroups.length === 0 && activeFilters.length > 0 && (
                      <Box 
                        sx={{ 
                          p: 4, 
                          textAlign: 'center', 
                          color: 'text.secondary',
                          bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                          borderRadius: 3,
                          border: '1px dashed',
                          borderColor: 'divider',
                        }}
                      >
                        <FilterListIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                        <Typography variant="body1">No matching categories</Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                          Try selecting different filters or clear all filters
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={clearFilters}
                          sx={{ mt: 2 }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                    )}

                    {filteredGroups.map((group, groupIndex) => {
                      return renderCard(group, groupIndex, groupIndex);
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          )}


        </Box>
      </Fade>
    </Box>
  );
});

export default HotelDetails; 